import {inject, Injectable, signal} from '@angular/core';
import {Confirmation, ConfirmationService} from 'primeng/api';
import {BasicTaDetails, FullTaDetails} from '../api';
import {TaSubmissionDatasource} from '../datasources/ta-submission.datasource';
import {Router} from '@angular/router';
import {finalize, Observable, Subscriber} from 'rxjs';
import {TaAppStore} from '../state/clcta-store';
import {DialogService} from 'primeng/dynamicdialog';
import {CancelTaDialog, CancelTaDialogData} from '../app/components/cancel-ta-dialog/cancel-ta-dialog';
import {isZeroValueDayTripItinerary} from '../util/itinerary.utils';

@Injectable({
  providedIn: 'root'
})
export class ConfirmationWrapperService {
  confirmationService = inject(ConfirmationService);
  taSubmissionDatasource = inject(TaSubmissionDatasource);
  dialogService = inject(DialogService);
  router = inject(Router);
  store = inject(TaAppStore);

  /** Set while a dialog's accepted request is outstanding. Internal to the accept guard below. */
  private inFlight = signal(false);

  /**
   * Opens a confirm dialog, clearing the guard first: it protects a single dialog's accept button,
   * and a newly opened dialog is always a fresh intent, so a stale flag can never wedge the UI.
   */
  private showConfirm(config: Confirmation): void {
    this.inFlight.set(false);
    this.confirmationService.confirm(config);
  }

  /**
   * Wraps an accept callback so a second click is dropped. PrimeNG leaves the accept button mounted
   * and clickable through the dialog's hide animation, so without this a fast double click fires the
   * request twice.
   */
  private acceptOnce<T>(observer: Subscriber<void>, request: () => Observable<T>): () => void {
    return () => {
      if (this.inFlight()) return;
      this.inFlight.set(true);
      request()
        .pipe(finalize(() => this.inFlight.set(false)))
        .subscribe({
          next: () => observer.next(),
          error: (err) => observer.error(err),
          complete: () => observer.complete(),
        });
    };
  }

  /**
   * Same guard for the acquit path, whose datasource call subscribes internally and returns void.
   * Nothing signals completion, so the flag clears on the next showConfirm() rather than on settle.
   */
  private acceptOnceVoid(action: () => void): () => void {
    return () => {
      if (this.inFlight()) return;
      this.inFlight.set(true);
      action();
    };
  }

  cancelConfirm(taSubmission: FullTaDetails | undefined): Observable<void> {
    if (taSubmission && taSubmission.isPrePaid && taSubmission.prePaymentProcessed) {
      return this.openCancelDialog(taSubmission, 'Cancel a Prepaid TA', 'Cancel Prepaid TA','prepaid');
    }
    return this.openCancelDialog(taSubmission, 'Confirm Cancel', 'Cancel TA', 'default');
  }

  private openCancelDialog(
    taSubmission: FullTaDetails | undefined,
    header: string,
    acceptLabel: string,
    type: string,
  ): Observable<void> {
    return this.openReasonDialog(header, {acceptLabel, type}, (notes) =>
      this.taSubmissionDatasource.cancelSubmission(
        taSubmission?.primaryTraveller?.travellerId ?? '', notes));
  }

  /**
   * Finance/admin undoing a cancellation raised in error, from either page of the cancel flow. The
   * server refuses it without a reason, so it takes the reason dialog rather than showConfirm().
   */
  revertCancellationConfirm(taSubmission: FullTaDetails | BasicTaDetails | undefined): Observable<void> {
    return this.openReasonDialog(
      'Confirm Reverse TA Cancellation',
      {
        acceptLabel: 'Reverse Cancellation',
        type: 'revert',
        promptLabel: 'Please provide a brief description of why you are reversing this cancellation',
        placeholder: 'Click here to add a reason for reversing this cancellation.',
      },
      (notes) => this.taSubmissionDatasource.revertTaCancellation(notes, taSubmission));
  }

  // Cancelling a prepayment request needs a reason; a processed one gets an extra warning.
  cancelPrepaymentConfirm(taSubmission: FullTaDetails | BasicTaDetails | undefined, processed: boolean): Observable<void> {
    return this.openReasonDialog(
      'Cancel Prepayment',
      {
        acceptLabel: 'Cancel Prepayment',
        type: processed ? 'cancelPrepaymentProcessed' : 'cancelPrepayment',
        promptLabel: 'Reason for cancelling the prepayment',
        placeholder: 'e.g. TA approved after travel, paid at finalisation',
      },
      (notes) => this.taSubmissionDatasource.cancelTaPrepayment(notes, taSubmission));
  }

  /**
   * Actions that require a reason, which the shared confirm dialog cannot collect - it renders a
   * plain message string and its accept button is not reactively disableable - so these use
   * CancelTaDialog instead of showConfirm(). Same contract as the other confirms: the observable
   * completes without emitting when the user backs out.
   */
  private openReasonDialog(
    header: string,
    data: CancelTaDialogData,
    request: (notes: string) => Observable<unknown>,
  ): Observable<void> {
    return new Observable<void>((observer) => {
      // showConfirm() normally clears the guard on open; this path never calls it, so a flag left
      // set by an earlier dialog would silently swallow the action.
      this.inFlight.set(false);

      const ref = this.dialogService.open(CancelTaDialog, {
        header,
        data,
        modal: true,
        closable: true,
        width: '40rem',
        contentStyle: {overflow: 'auto'},
      });

      if (!ref) {
        console.error('Failed to open the ' + header + ' dialog.');
        observer.complete();
        return;
      }

      ref.onClose.subscribe((notes?: string) => {
        if (!notes) {
          observer.complete(); // user cancelled
          return;
        }
        this.acceptOnce(observer, () => request(notes))();
      });
    });
  }

  deleteConfirm(): Observable<void> {
    return new Observable<void>((observer) => {
      this.showConfirm({
        message: 'Are you sure you want to delete this Travel Allowance application? This cannot be undone.',
        header: 'Confirm Delete',
        icon: 'pi pi-info-circle',
        rejectLabel: 'Close',
        rejectButtonProps: {
          label: 'Close',
          severity: 'secondary',
          outlined: true,
        },
        acceptButtonProps: {
          label: 'Delete TA',
          severity: 'danger',
        },

        accept: this.acceptOnce(observer, () => this.taSubmissionDatasource.deleteInProgressSubmission()),
        reject: () => {
          observer.complete(); // user cancelled
        },
      });
    });
  }

  approveConfirm(
    action: 'approve' | 'withhold' | 'reject',
    notes: string,
    taSubmission: FullTaDetails | BasicTaDetails | undefined,
  ): Observable<void> {

    return new Observable<void>((observer) => {
      let formattedAction = action[0].toUpperCase() + action.slice(1);
      const severityMap = {
        approve: 'primary',
        withhold: 'warn',
        reject: 'danger'
      };
      const iconMap = {
        approve: 'pi pi-check-circle',
        withhold: 'pi pi-exclamation-circle',
        reject: 'pi pi-thumbs-down'
      };
      this.showConfirm({
        message: 'Are you sure you want to ' + action + ' this Travel Allowance application?',
        header: 'Confirm ' + formattedAction + ' TA',
        icon: iconMap[action],

        rejectLabel: 'Close',
        rejectButtonProps: {
          label: 'Close',
          severity: 'secondary',
          outlined: true,
        },
        acceptButtonProps: {
          label: formattedAction + ' TA',
          severity: severityMap[action],
        },
        accept: this.acceptOnce(observer, () => this.taSubmissionDatasource.approveTa(action, notes, taSubmission)),
        reject: () => {
          observer.complete(); // user cancelled
        }
      });
    });
  }

  acquitConfirm(travellerId: string, comments: string, taSubmission: FullTaDetails | undefined): void {
    let msg: string;
    const traveller = taSubmission?.travellers?.find(traveller => traveller.travellerId === travellerId);
    if (!traveller) {
      msg = 'Are you sure you want to acquit this portion of the Travel Allowance?'
    } else if (traveller.details?.id === this.store.session().details?.user?.id) {
      // Name the traveller whenever it is not the person clicking - a booking officer acquitting a
      // single-traveller delegate TA needs to see whose travel they are certifying.
      msg = taSubmission?.travellers?.length === 1
        ? 'Are you sure you want to acquit this Travel Allowance?'
        : 'Are you sure you want to acquit your travel in this Travel Allowance?'
    } else {
      msg = 'Are you sure you want to acquit '+ traveller.details?.name +'\'s travel in this Travel Allowance?'
    }

    // TAStore skips acquittal approval for a $0 single day trip that was not pre-paid. Only stated
    // for a single traveller: on group travel the other travellers' itineraries decide it.
    if (taSubmission?.travellers?.length === 1 && !taSubmission.isPrePaid
      && isZeroValueDayTripItinerary(this.store.currentItinerary())) {
      msg += ' This $0 day trip will be finalised straight away, with no acquittal approval.';
    }

    this.showConfirm({
      message: msg,
      header: 'Confirm acquit TA',
      icon: 'pi pi-check-circle',

      rejectLabel: 'Close',
      rejectButtonProps: {
        label: 'Close',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Acquit TA',
        severity: 'primary',
      },
      accept: this.acceptOnceVoid(() => this.taSubmissionDatasource.acquitTa(travellerId, comments)),
      reject: () => {},
    });
  }

  approveAcquittalConfirm(action: 'approve' | 'withhold', notes: string, taSubmission: FullTaDetails | BasicTaDetails | undefined): Observable<void> {
    return new Observable<void>((observer) => {
      let formattedAction = action[0].toUpperCase() + action.slice(1);
      const severityMap = {
        approve: 'primary',
        withhold: 'warn',
      };
      const iconMap = {
        approve: 'pi pi-check-circle',
        withhold: 'pi pi-exclamation-circle',
      };
      this.showConfirm({
        message: 'Are you sure you want to ' + action + ' this Travel Allowance acquittal?',
        header: 'Confirm ' + formattedAction + ' TA acquittal',
        icon: iconMap[action],

        rejectLabel: 'Close',
        rejectButtonProps: {
          label: 'Close',
          severity: 'secondary',
          outlined: true,
        },
        acceptButtonProps: {
          label: formattedAction + ' TA acquittal',
          severity: severityMap[action],
        },
        accept: this.acceptOnce(observer, () =>
          this.taSubmissionDatasource.approveTaAcquittal(action, notes, taSubmission)),
        reject: () => {
          observer.complete(); // user cancelled
        },
      });
    });
  }

  /**
   * Acknowledging the cancellation of a prepaid TA whose payment has already been processed.
   * Acknowledge is the only action - the cancellation itself was already decided, this signs off
   * the deduction - so unlike approveAcquittalConfirm there is no withhold variant.
   *
   * "Acknowledge" is the user-facing word for it, matching the buttons on the manage page and the
   * finance info card. The method, the datasource call and the server all still say approve: it is
   * an ApprovalAction.Approve against a Cancellation approval row.
   */
  approveCancellationConfirm(notes: string, taSubmission: FullTaDetails | BasicTaDetails | undefined): Observable<void> {
    return new Observable<void>((observer) => {
      this.showConfirm({
        message: 'Are you sure you want to acknowledge the cancellation of this Travel Allowance? The prepayment will be recovered from the traveller.',
        header: 'Confirm Acknowledge TA cancellation',
        icon: 'pi pi-check-circle',
        rejectLabel: 'Close',
        rejectButtonProps: {
          label: 'Close',
          severity: 'secondary',
          outlined: true,
        },
        acceptButtonProps: {
          label: 'Acknowledge TA cancellation',
          severity: 'primary',
        },
        accept: this.acceptOnce(observer, () =>
          this.taSubmissionDatasource.approveTaCancellation(notes, taSubmission)),
        reject: () => {
          observer.complete(); // user cancelled
        },
      });
    });
  }

  processPrepaymentConfirm(taSubmission?: FullTaDetails | BasicTaDetails): Observable<void> {
    return new Observable<void>((observer) => {
      this.showConfirm({
        message: 'Are you sure you want to mark this TA as having prepayment processed?',
        header: 'Confirm Prepayment Processed',
        icon: 'pi pi-check-circle',
        rejectLabel: 'Close',
        rejectButtonProps: {
          label: 'Close',
          severity: 'secondary',
          outlined: true,
        },
        acceptButtonProps: {
          label: 'Confirm',
          severity: 'primary',
        },
        accept: this.acceptOnce(observer, () => this.taSubmissionDatasource.processTaPrepayment(taSubmission)),
        reject: () => {
          observer.complete();
        },
      });
    });
  }

  /**
   * Batch forms of finaliseConfirm and processPrepaymentConfirm for the finance dashboard's ticked
   * rows. Finalise is the only action offered - withholding stays a per-TA decision.
   */
  finaliseBatchConfirm(taSubmissions: BasicTaDetails[]): Observable<void> {
    return new Observable<void>((observer) => {
      const count = taSubmissions.length;
      // The payment tab lists approved cancellations alongside approved acquittals.
      const cancellations = taSubmissions.filter(taSubmission => taSubmission.status === 'CancelApproved').length;
      let message = 'Are you sure you want to finalise the ' + count + ' selected TA ' + this.pluralise('application', count) + '?';
      if (cancellations > 0) {
        message += ' This includes ' + cancellations + ' approved ' + this.pluralise('cancellation', cancellations) + ', which will be closed as cancelled.';
      }
      this.showConfirm({
        message,
        header: 'Confirm Finalise Selected TAs',
        icon: 'pi pi-check-circle',
        rejectLabel: 'Close',
        rejectButtonProps: {
          label: 'Close',
          severity: 'secondary',
          outlined: true,
        },
        acceptButtonProps: {
          label: 'Finalise Selected TAs',
          severity: 'primary',
        },
        accept: this.acceptOnce(observer, () => this.taSubmissionDatasource.finaliseSubmissionsBatch(taSubmissions)),
        reject: () => {
          observer.complete(); // user cancelled
        },
      });
    });
  }

  processPrepaymentBatchConfirm(taSubmissions: BasicTaDetails[]): Observable<void> {
    return new Observable<void>((observer) => {
      const count = taSubmissions.length;
      this.showConfirm({
        message: 'Are you sure you want to mark the ' + count + ' selected ' + this.pluralise('TA', count) + ' as having prepayment processed?',
        header: 'Confirm Prepayment Processing',
        icon: 'pi pi-check-circle',
        rejectLabel: 'Close',
        rejectButtonProps: {
          label: 'Close',
          severity: 'secondary',
          outlined: true,
        },
        acceptButtonProps: {
          label: 'Confirm',
          severity: 'primary',
        },
        accept: this.acceptOnce(observer, () => this.taSubmissionDatasource.processTaPrepaymentsBatch(taSubmissions)),
        reject: () => {
          observer.complete();
        },
      });
    });
  }

  processDayTripPaymentConfirm(taSubmission?: FullTaDetails | BasicTaDetails): Observable<void> {
    return new Observable<void>((observer) => {
      this.showConfirm({
        message: 'Are you sure you want to mark this TA as having the day trip payment processed? A single day trip TA will also be finalised.',
        header: 'Confirm Day Trip Payment Processed',
        icon: 'pi pi-check-circle',
        rejectLabel: 'Close',
        rejectButtonProps: {
          label: 'Close',
          severity: 'secondary',
          outlined: true,
        },
        acceptButtonProps: {
          label: 'Confirm',
          severity: 'primary',
        },
        accept: this.acceptOnce(observer, () => this.taSubmissionDatasource.processDayTripPayment(taSubmission)),
        reject: () => {
          observer.complete();
        },
      });
    });
  }

  processDayTripPaymentBatchConfirm(taSubmissions: BasicTaDetails[]): Observable<void> {
    return new Observable<void>((observer) => {
      const count = taSubmissions.length;
      this.showConfirm({
        message: 'Are you sure you want to mark the ' + count + ' selected ' + this.pluralise('TA', count) + ' as having the day trip payment processed? Single day trip TAs will also be finalised.',
        header: 'Confirm Day Trip Payment Processing',
        icon: 'pi pi-check-circle',
        rejectLabel: 'Close',
        rejectButtonProps: {
          label: 'Close',
          severity: 'secondary',
          outlined: true,
        },
        acceptButtonProps: {
          label: 'Confirm',
          severity: 'primary',
        },
        accept: this.acceptOnce(observer, () => this.taSubmissionDatasource.processDayTripPaymentsBatch(taSubmissions)),
        reject: () => {
          observer.complete();
        },
      });
    });
  }

  private pluralise(noun: string, count: number): string {
    return count === 1 ? noun : noun + 's';
  }

  processDeductionConfirm(taSubmission?: FullTaDetails | BasicTaDetails): Observable<void> {
    return new Observable<void>((observer) => {
      this.showConfirm({
        message: 'Are you sure you want to mark this TA as having overpayment deduction processed?',
        header: 'Confirm Overpayment Deduction Processed',
        icon: 'pi pi-check-circle',
        rejectLabel: 'Close',
        rejectButtonProps: {
          label: 'Close',
          severity: 'secondary',
          outlined: true,
        },
        acceptButtonProps: {
          label: 'Confirm',
          severity: 'primary',
        },
        accept: this.acceptOnce(observer, () =>
          this.taSubmissionDatasource.processTaOverpaymentDeduction(taSubmission)),
        reject: () => {
          observer.complete();
        },
      });
    });
  }

  /**
   * Finalising an approved cancellation - the same server endpoint as finaliseConfirm, which
   * branches on the TA's status, so this reuses the datasource's finalise call. Finalise is the
   * only action: resetFinalise refuses a CancelApproved TA, so there is no withhold variant.
   */
  finaliseCancellationConfirm(notes: string, taSubmission: FullTaDetails | BasicTaDetails | undefined): Observable<void> {
    return new Observable<void>((observer) => {
      this.showConfirm({
        message: 'Are you sure you want to finalise the cancellation of this Travel Allowance? The TA will be closed as cancelled.',
        header: 'Confirm Finalise TA cancellation',
        icon: 'pi pi-check-circle',
        rejectLabel: 'Close',
        rejectButtonProps: {
          label: 'Close',
          severity: 'secondary',
          outlined: true,
        },
        acceptButtonProps: {
          label: 'Finalise TA cancellation',
          severity: 'primary',
        },
        accept: this.acceptOnce(observer, () =>
          this.taSubmissionDatasource.finaliseSubmission('finalise', notes, taSubmission)),
        reject: () => {
          observer.complete(); // user cancelled
        },
      });
    });
  }

  finaliseConfirm(action: 'finalise' | 'withhold', notes: string, taSubmission: FullTaDetails | BasicTaDetails | undefined): Observable<void> {
    return new Observable<void>((observer) => {
      let formattedAction = action[0].toUpperCase() + action.slice(1);
      const severityMap = {
        finalise: 'primary',
        withhold: 'warn',
      };
      const iconMap = {
        finalise: 'pi pi-check-circle',
        withhold: 'pi pi-exclamation-circle',
      };
      this.showConfirm({
        message: 'Are you sure you want to ' + action + ' this Travel Allowance application?',
        header: 'Confirm ' + formattedAction + ' TA',
        icon: iconMap[action],
        rejectLabel: 'Close',
        rejectButtonProps: {
          label: 'Close',
          severity: 'secondary',
          outlined: true,
        },
        acceptButtonProps: {
          label: formattedAction + ' TA',
          severity: severityMap[action],
        },
        accept: this.acceptOnce(observer, () =>
          this.taSubmissionDatasource.finaliseSubmission(action, notes, taSubmission)),
        reject: () => {
          observer.complete(); // user cancelled
        },
      });
    });
  }

}
